export type RequestedTeachingRole = 'lead' | 'co_teacher';

export type ActiveCoverage = {
  requestId: string;
  requestedRole: RequestedTeachingRole;
  requestedTeacherId: string;
  status: 'open' | 'claimed';
  substituteTeacherId?: string;
  incumbentCoverage?: Omit<ActiveCoverage, 'incumbentCoverage'>;
};

export type EventStaffingLike = {
  mainTeacherId?: string | null;
  supportingTeacherIds?: string[] | null;
  activeCoverage?: ActiveCoverage[] | null;
};

export type EffectiveEventStaffing = {
  effectiveLeadTeacherId: string | null;
  effectiveSupportingTeacherIds: string[];
  coverage: ActiveCoverage | null;
};

/**
 * Missing activeCoverage is deliberately legacy behavior: stored event fields
 * are already the effective assignment for every historical event.
 */
export function resolveEffectiveEventStaffing(event: EventStaffingLike): EffectiveEventStaffing {
  const assignedLead = typeof event.mainTeacherId === 'string' && event.mainTeacherId.trim()
    ? event.mainTeacherId.trim()
    : null;
  const assignedSupport = Array.from(new Set(
    (Array.isArray(event.supportingTeacherIds) ? event.supportingTeacherIds : [])
      .map(String)
      .map(id => id.trim())
      .filter(Boolean),
  ));
  const coverage = Array.isArray(event.activeCoverage) && event.activeCoverage.length > 0
    ? event.activeCoverage[0]
    : null;

  // An open handoff request keeps the incumbent substitute effective until the
  // next substitute actually claims the class.
  const effectiveCoverage = coverage?.status === 'open' && coverage.incumbentCoverage?.status === 'claimed'
    ? coverage.incumbentCoverage
    : coverage;

  if (!effectiveCoverage || effectiveCoverage.status !== 'claimed' || !String(effectiveCoverage.substituteTeacherId || '').trim()) {
    return {
      effectiveLeadTeacherId: assignedLead,
      effectiveSupportingTeacherIds: assignedSupport,
      coverage,
    };
  }

  const substituteTeacherId = String(effectiveCoverage.substituteTeacherId).trim();
  if (effectiveCoverage.requestedRole === 'lead') {
    return {
      effectiveLeadTeacherId: substituteTeacherId,
      effectiveSupportingTeacherIds: assignedSupport,
      coverage,
    };
  }

  const requestedTeacherId = String(effectiveCoverage.requestedTeacherId || '').trim();
  return {
    effectiveLeadTeacherId: assignedLead,
    effectiveSupportingTeacherIds: Array.from(new Set([
      ...assignedSupport.filter(id => id !== requestedTeacherId),
      substituteTeacherId,
    ])),
    coverage,
  };
}

/** Keep the teachers involved in coverage, including an in-progress handoff. */
export function isSupportingAssignmentCovered(id: string, coverage: ActiveCoverage[] | null | undefined): boolean {
  return (coverage || []).some(item => [
    item.requestedTeacherId, item.substituteTeacherId,
    item.incumbentCoverage?.requestedTeacherId, item.incumbentCoverage?.substituteTeacherId,
  ].includes(id));
}
